import { MarkerType } from 'reactflow';
import dagre from 'dagre';
import ELK from 'elkjs/lib/elk.bundled.js';

const STAGE_WIDTH = 280;
const STEP_WIDTH = 240;
const STEPVAR_WIDTH = 220;
const VERTICAL_SPACING = 180;
const HORIZONTAL_SPACING = 350;
const STEP_VAR_VERTICAL_SPACING = 100;

const NODE_WIDTH = 280;
const NODE_HEIGHT = 120;
const STEP_VAR_NODE_HEIGHT = 140;

// Настройки плотности раскладки
const DENSITY_PRESETS = {
  compact: { nodesep: 50, ranksep: 100, edgesep: 50 },
  standard: { nodesep: 100, ranksep: 150, edgesep: 80 },
  spacious: { nodesep: 150, ranksep: 200, edgesep: 100 }
};

// Dagre layout
function getDagreLayout(nodes, edges, options = {}) {
  const { direction = 'LR', density = 'standard' } = options;
  const densityConfig = DENSITY_PRESETS[density] || DENSITY_PRESETS.standard;
  
  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));
  dagreGraph.setGraph({ 
    rankdir: direction,
    ...densityConfig,
    marginx: 50,
    marginy: 50
  });

  nodes.forEach((node) => {
    const height = node.type === 'stepVarNode' ? STEP_VAR_NODE_HEIGHT : NODE_HEIGHT;
    dagreGraph.setNode(node.id, { width: NODE_WIDTH, height });
  });

  edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  dagre.layout(dagreGraph);

  const layoutedNodes = nodes.map((node) => {
    const nodeWithPosition = dagreGraph.node(node.id);
    return {
      ...node,
      position: {
        x: nodeWithPosition.x - NODE_WIDTH / 2,
        y: nodeWithPosition.y - (node.type === 'stepVarNode' ? STEP_VAR_NODE_HEIGHT : NODE_HEIGHT) / 2,
      },
    };
  });

  return { nodes: layoutedNodes, edges };
}

// ELK layout
async function getELKLayout(nodes, edges, options = {}) {
  const { direction = 'RIGHT', density = 'standard' } = options;
  const densityConfig = DENSITY_PRESETS[density] || DENSITY_PRESETS.standard;
  
  const elk = new ELK();
  
  // Преобразование direction для ELK
  const elkDirection = {
    'LR': 'RIGHT',
    'RL': 'LEFT',
    'TB': 'DOWN',
    'BT': 'UP'
  }[direction] || 'RIGHT';
  
  const graph = {
    id: 'root',
    layoutOptions: {
      'elk.algorithm': 'layered',
      'elk.direction': elkDirection,
      'elk.spacing.nodeNode': densityConfig.nodesep.toString(),
      'elk.layered.spacing.nodeNodeBetweenLayers': densityConfig.ranksep.toString(),
      'elk.spacing.edgeNode': densityConfig.edgesep.toString(),
      'elk.padding': '[top=50,left=50,bottom=50,right=50]'
    },
    children: nodes.map(node => ({
      id: node.id,
      width: NODE_WIDTH,
      height: node.type === 'stepVarNode' ? STEP_VAR_NODE_HEIGHT : NODE_HEIGHT
    })),
    edges: edges.map(edge => ({
      id: edge.id,
      sources: [edge.source],
      targets: [edge.target]
    }))
  };

  const layoutedGraph = await elk.layout(graph);
  
  const layoutedNodes = nodes.map(node => {
    const layoutedNode = layoutedGraph.children.find(n => n.id === node.id);
    return {
      ...node,
      position: {
        x: layoutedNode.x,
        y: layoutedNode.y
      }
    };
  });

  return { nodes: layoutedNodes, edges };
}

// Универсальная функция раскладки
export async function getLayoutedElements(nodes, edges, options = {}) {
  const { algorithm = 'dagre', direction = 'LR', density = 'standard' } = options;
  
  if (algorithm === 'elk') {
    return await getELKLayout(nodes, edges, { direction, density });
  } else {
    return getDagreLayout(nodes, edges, { direction, density });
  }
}

export async function scriptToFlowchart(script, savedPositions = {}, layoutOptions = {}) {
  const nodes = [];
  const edges = [];
  
  if (!script?.Main_Script?.Stages) {
    return { nodes, edges };
  }

  const stages = script.Main_Script.Stages;
  const useAutoLayout = Object.keys(savedPositions).length === 0;
  const { 
    edgeType = 'default',
    algorithm = 'dagre',
    direction = 'LR',
    density = 'standard'
  } = layoutOptions;
  
  nodes.push({
    id: 'start',
    type: 'input',
    data: { label: 'START' },
    position: { x: 0, y: 0 },
    style: { 
      background: '#10b981', 
      color: 'white', 
      border: '2px solid #059669',
      borderRadius: '8px',
      padding: '10px 20px',
      fontWeight: 'bold'
    }
  });

  stages.forEach((stage, stageIndex) => {
    const stageNodeId = stage.UUID || `stage-${stageIndex}`;
    
    nodes.push({
      id: stageNodeId,
      type: 'stageNode',
      data: {
        label: stage.Stage_Name || `Этап ${stage.Stage_Number || stageIndex + 1}`,
        stageNumber: stage.Stage_Number || stageIndex + 1,
        description: stage.Stage_Description,
        skipIf: stage.Stage_Skip_If,
        variants: stage.Stage_Vars?.length || 0,
        stageIndex,
        variantCount: stage.Stage_Vars?.length || 0
      },
      position: { x: 0, y: 0 },
      draggable: true,
      sourcePosition: 'right',
      targetPosition: 'left'
    });

    if (stageIndex === 0) {
      edges.push({
        id: `start-to-${stageNodeId}`,
        source: 'start',
        target: stageNodeId,
        type: edgeType,
        animated: true,
        markerEnd: { type: MarkerType.ArrowClosed, color: '#3b82f6' },
        style: { stroke: '#3b82f6', strokeWidth: 2 }
      });
    } else {
      const prevStageId = stages[stageIndex - 1].UUID || `stage-${stageIndex - 1}`;
      edges.push({
        id: `${prevStageId}-to-${stageNodeId}`,
        source: prevStageId,
        target: stageNodeId,
        type: edgeType,
        markerEnd: { type: MarkerType.ArrowClosed, color: '#3b82f6' },
        style: { stroke: '#3b82f6', strokeWidth: 2 }
      });
    }

    if (stage.Stage_Vars && stage.Stage_Vars.length > 0) {
      stage.Stage_Vars.forEach((variant, variantIndex) => {
        const steps = variant.Steps || [];
        
        steps.forEach((step, stepIndex) => {
          const stepNodeId = step.UUID || `${stageNodeId}-v${variantIndex}-step-${stepIndex}`;
          
          nodes.push({
            id: stepNodeId,
            type: 'stepNode',
            data: {
              label: step.Step_Name || `Шаг ${step.Step_Number || stepIndex + 1}`,
              stepNumber: step.Step_Number || stepIndex + 1,
              description: step.Step_Description,
              skipIf: step.Step_Skip_If,
              vars: step.Step_Vars?.length || 0,
              stageIndex,
              variantIndex,
              stepIndex,
              variantName: variant.Stage_Name || `Вариант ${variantIndex + 1}`
            },
            position: { x: 0, y: 0 },
            draggable: true,
            sourcePosition: 'right',
            targetPosition: 'left'
          });

          if (stepIndex === 0) {
            edges.push({
              id: `${stageNodeId}-to-${stepNodeId}`,
              source: stageNodeId,
              target: stepNodeId,
              type: edgeType,
              markerEnd: { type: MarkerType.ArrowClosed, color: '#8b5cf6' },
              style: { stroke: '#8b5cf6', strokeWidth: 2 },
              label: variant.Stage_Name || `v${variantIndex + 1}`,
              labelStyle: { fill: '#8b5cf6', fontWeight: 500 },
              labelBgStyle: { fill: '#f3f4f6' }
            });
          } else {
            const prevStepId = steps[stepIndex - 1].UUID || `${stageNodeId}-v${variantIndex}-step-${stepIndex - 1}`;
            edges.push({
              id: `${prevStepId}-to-${stepNodeId}`,
              source: prevStepId,
              target: stepNodeId,
              type: edgeType,
              markerEnd: { type: MarkerType.ArrowClosed, color: '#8b5cf6' },
              style: { stroke: '#8b5cf6', strokeWidth: 2 }
            });
          }

          if (step.Step_Vars && step.Step_Vars.length > 0) {
            step.Step_Vars.forEach((stepVar, stepVarIndex) => {
              const stepVarNodeId = stepVar.Var_UUID || `${stepNodeId}-var-${stepVarIndex}`;
              
              nodes.push({
                id: stepVarNodeId,
                type: 'stepVarNode',
                data: {
                  label: stepVar.Step_Var_Name || `Вариант ${stepVarIndex + 1}`,
                  condition: stepVar.Step_Var_If,
                  task: stepVar.Task_Settings?.Task,
                  status: stepVar.Task_Settings?.Status,
                  approvedPhrases: stepVar.Task_Settings?.Approved_Phrases,
                  stageIndex,
                  variantIndex,
                  stepIndex,
                  stepVarIndex
                },
                position: { x: 0, y: 0 },
                draggable: true,
                sourcePosition: 'right',
                targetPosition: 'left'
              });

              edges.push({
                id: `${stepNodeId}-to-${stepVarNodeId}`,
                source: stepNodeId,
                target: stepVarNodeId,
                type: edgeType,
                label: stepVar.Step_Var_If ? 'условие' : '',
                markerEnd: { type: MarkerType.ArrowClosed, color: '#10b981' },
                style: { stroke: '#10b981', strokeWidth: 2 },
                labelStyle: { fill: '#10b981', fontWeight: 500 },
                labelBgStyle: { fill: '#f3f4f6' }
              });
            });
          }
        });
      });
    }
  });

  nodes.push({
    id: 'end',
    type: 'output',
    data: { label: 'END' },
    position: { x: 0, y: 0 },
    style: { 
      background: '#ef4444', 
      color: 'white', 
      border: '2px solid #dc2626',
      borderRadius: '8px',
      padding: '10px 20px',
      fontWeight: 'bold'
    }
  });

  if (stages.length > 0) {
    const lastStageId = stages[stages.length - 1]?.UUID || `stage-${stages.length - 1}`;
    edges.push({
      id: `${lastStageId}-to-end`,
      source: lastStageId,
      target: 'end',
      type: edgeType,
      markerEnd: { type: MarkerType.ArrowClosed, color: '#3b82f6' },
      style: { stroke: '#3b82f6', strokeWidth: 2 }
    });
  }

  if (useAutoLayout) {
    const layouted = await getLayoutedElements(nodes, edges, { algorithm, direction, density });
    return layouted;
  } else {
    const nodesWithSavedPositions = nodes.map(node => ({
      ...node,
      position: savedPositions[node.id] || node.position
    }));
    return { nodes: nodesWithSavedPositions, edges };
  }
}

// Преобразование позиций узлов в формат для сохранения
export function extractNodePositions(nodes) {
  const positions = {};
  nodes.forEach(node => {
    positions[node.id] = { x: node.position.x, y: node.position.y };
  });
  return positions;
}

export function truncateText(text, maxLength = 50) {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
}

export function getStatusColor(status) {
  const statusColors = {
    'Conversation_Started': '#3b82f6',
    'Talking': '#10b981',
    'Qualified': '#8b5cf6',
    'Ready_For_Donno_Live': '#f59e0b',
    'Scheduled_Donno_Live': '#06b6d4',
    'Want_To_Call': '#ec4899',
    'Closed': '#ef4444',
    'Rejected': '#6b7280'
  };
  return statusColors[status] || '#64748b';
}
